import { createClient, type SanityClient } from "@sanity/client"
import { createImageUrlBuilder } from "@sanity/image-url"
import type { SanityImageSource } from "@sanity/image-url"

/** Default Wundrlst CMS project; override with `VITE_SANITY_PROJECT_ID` for staging or forks. */
const DEFAULT_SANITY_PROJECT_ID = "pvxcfu1h"

const projectId =
  (import.meta.env.VITE_SANITY_PROJECT_ID as string | undefined)?.trim() ||
  DEFAULT_SANITY_PROJECT_ID
const dataset =
  (import.meta.env.VITE_SANITY_DATASET as string | undefined) || "production"

const apiVersion = "2024-01-01"

/**
 * In Vite dev, rewrite `*.api.sanity.io` requests to same-origin `/__sanity-api` (see
 * `vite.config.ts` proxy). `@sanity/client` v7 no longer accepts a custom `fetch` in
 * `createClient`, so we patch `window.fetch` once before the client is created.
 */
function installSanityDevFetchProxy(): void {
  if (!import.meta.env.DEV || typeof window === "undefined") return
  const w = window as Window & { __wundrlstSanityFetchProxy?: boolean }
  if (w.__wundrlstSanityFetchProxy) return
  w.__wundrlstSanityFetchProxy = true
  const original = window.fetch.bind(window)
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url
    if (url.includes(".api.sanity.io")) {
      const { pathname, search } = new URL(url)
      return original(`/__sanity-api${pathname}${search}`, init)
    }
    return original(input, init)
  }
}

installSanityDevFetchProxy()

export function isSanityConfigured(): boolean {
  return projectId.length > 0
}

/**
 * CDN speeds up public reads in production but can delay fresh content by a minute or more.
 * Set `VITE_SANITY_USE_CDN=false` in `.env.local` (or deploy env) to read straight from the API while editing CMS content.
 */
function sanityUseCdn(): boolean {
  const raw = (import.meta.env.VITE_SANITY_USE_CDN as string | undefined)?.trim().toLowerCase()
  if (raw === "false" || raw === "0") return false
  if (raw === "true" || raw === "1") return true
  return Boolean(import.meta.env.PROD)
}

/** Public connection info for dev diagnostics (no tokens). */
export function getSanityPublishConfig(): {
  projectId: string
  dataset: string
  useCdn: boolean
} {
  return {
    projectId: projectId.trim(),
    dataset,
    useCdn: sanityUseCdn(),
  }
}

/** Hosted Sanity Studio URL for the configured project (or default). */
export function getSanityStudioUrl(): string {
  const id =
    (import.meta.env.VITE_SANITY_PROJECT_ID as string | undefined)?.trim() ||
    DEFAULT_SANITY_PROJECT_ID
  return `https://${id}.sanity.studio`
}

/**
 * Anonymous client for published/draft-visible content permitted by dataset rules.
 * Never pass API tokens — they would ship in the client bundle (`import.meta.env` is public).
 */
export const sanityClient: SanityClient | null = isSanityConfigured()
  ? createClient({
      projectId: projectId.trim(),
      dataset,
      useCdn: sanityUseCdn(),
      apiVersion,
    })
  : null

const imageBuilder = isSanityConfigured()
  ? createImageUrlBuilder({ projectId: projectId.trim(), dataset })
  : null

export function urlForImage(
  source: SanityImageSource | null | undefined
): string | undefined {
  if (!source || !imageBuilder) return undefined
  try {
    return imageBuilder.image(source).width(1200).quality(82).url()
  } catch {
    return undefined
  }
}
